<script setup>
import { computed } from 'vue'
import { useConveyancingCaseStore } from '~/stores/conveyancing-cases.js'

const props = defineProps({
  context: {
    type: Object,
    required: true,
  },
})

const conveyancingCaseStore = useConveyancingCaseStore()

const formData = computed(() => {
  let node = props.context.node
  while (node.parent) {
    node = node.parent
  }
  return node.value || {}
})

// Determine which client we're validating (client1, client2, client3, or client4)
const clientNumber = computed(() => {
  const nodeName = props.context.node.name
  // Extract client number from the node name (e.g., "clients.client2.address_warning" -> "client2")
  const match = nodeName.match(/clients\.(client\d+)/)
  return match ? match[1] : 'client1'
})

// Get the conveyancing case client address
const caseClientAddress = computed(() => {
  const clientKey = clientNumber.value === 'client1' ? 'client' : clientNumber.value
  return conveyancingCaseStore.conveyancingCase?.[clientKey]?.address_block
})

// Get the form client address - handles address reuse from client1
const addressToCompare = computed(() => {
  // For client2, client3, client4, check if they're using client1's address
  if (clientNumber.value !== 'client1') {
    const isAddressSameAsFirst = formData.value[`clients.${clientNumber.value}.is_address_same_as_first_client`]

    if (isAddressSameAsFirst === 'Yes' || isAddressSameAsFirst === true) {
      // Use client1's address
      return formData.value['clients.client1.address']?.full_address
    }
  }

  // Use the client's own address
  const addressKey = `clients.${clientNumber.value}.address`
  return formData.value[addressKey]?.full_address
})

const normalizeAddress = (str) =>
  str
    .toLowerCase()
    .replace(/[.,/#!$%^&*;:{}=\-_`~()]/g, '')
    .replace(/\s+/g, ' ')
    .trim()

const tokenize = (str) => str.split(' ').filter(Boolean)

const addressesMatch = (address1, address2) => {
  const normalized1 = normalizeAddress(address1)
  const normalized2 = normalizeAddress(address2)

  // Exact match
  if (normalized1 === normalized2) return true

  // Partial containment match
  if (normalized1.includes(normalized2) || normalized2.includes(normalized1)) return true

  // Token-based match (all case tokens present in entered address)
  const tokens1 = tokenize(normalized1)
  const tokens2 = tokenize(normalized2)

  return tokens2.every((token) => tokens1.includes(token))
}

const showAddressMismatchWarning = computed(() => {
  if (!caseClientAddress.value || !addressToCompare.value) return false

  return !addressesMatch(addressToCompare.value, caseClientAddress.value)
})
</script>

<template>
  <el-row v-if="showAddressMismatchWarning" class="mb-3 mt-1">
    <el-col :span="24">
      <el-alert type="warning" :closable="false" show-icon>
        The address you have entered differs from the client address on your case. Please ensure this is correct.
      </el-alert>
    </el-col>
  </el-row>
</template>
